import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { adminApi } from "@/api/admin";
import type {
  ChangeUserStatusPayload,
  CreateRolePayload,
  InviteUserPayload,
  UpdateRolePayload,
  UpdateUserPayload,
  UsersQueryParams,
} from "@/types/rbac";

// ============================
// Query Key Factories
// ============================

export const adminKeys = {
  all: ["admin"] as const,

  users: () => [...adminKeys.all, "users"] as const,
  userList: (params: UsersQueryParams) =>
    [...adminKeys.users(), params] as const,
  userDetail: (userId: string) => [...adminKeys.users(), userId] as const,

  roles: () => [...adminKeys.all, "roles"] as const,
  roleDetail: (roleId: string) => [...adminKeys.roles(), roleId] as const,

  permissions: () => [...adminKeys.all, "permissions"] as const,
};

// ============================
// User Hooks
// ============================

export function useUsers(params: UsersQueryParams) {
  return useQuery({
    queryKey: adminKeys.userList(params),
    queryFn: () => adminApi.getUsers(params),
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: InviteUserPayload) => adminApi.inviteUser(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
      queryClient.invalidateQueries({ queryKey: adminKeys.roles() });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      payload,
    }: {
      userId: string;
      payload: UpdateUserPayload;
    }) => adminApi.updateUser(userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
      queryClient.invalidateQueries({ queryKey: adminKeys.roles() });
    },
  });
}

export function useChangeUserStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      payload,
    }: {
      userId: string;
      payload: ChangeUserStatusPayload;
    }) => adminApi.changeUserStatus(userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => adminApi.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
      queryClient.invalidateQueries({ queryKey: adminKeys.roles() });
    },
  });
}

export function useResetTrustedDevices() {
  return useMutation({
    mutationFn: (userId: string) => adminApi.resetTrustedDevices(userId),
  });
}

export function useSendPasswordResetLink() {
  return useMutation({
    mutationFn: (userId: string) => adminApi.sendPasswordResetLink(userId),
  });
}

// ============================
// Role Hooks
// ============================

export function rolesQueryOptions() {
  return queryOptions({
    queryKey: adminKeys.roles(),
    queryFn: () => adminApi.getRoles(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useRoles() {
  return useQuery(rolesQueryOptions());
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRolePayload) => adminApi.createRole(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.roles() });
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      roleId,
      payload,
    }: {
      roleId: string;
      payload: UpdateRolePayload;
    }) => adminApi.updateRole(roleId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.roles() });
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) => adminApi.deleteRole(roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.roles() });
    },
  });
}

// ============================
// Permission Hooks
// ============================

export function permissionsQueryOptions() {
  return queryOptions({
    queryKey: adminKeys.permissions(),
    queryFn: () => adminApi.getPermissions(),
    staleTime: 30 * 60 * 1000,
  });
}

export function usePermissions() {
  return useQuery(permissionsQueryOptions());
}
